import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Award, Zap, ShieldCheck, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const stats = [
  { n: 'UK', l: 'residential customers' },
  { n: 'Plans', l: 'monthly or annual' },
  { n: 'Mon–Fri', l: '9am–5pm customer service' },
  { n: '2024', l: 'incorporated' },
];

const values = [
  { icon: Heart, title: 'Care', text: 'We treat every home like it\'s our own — with genuine warmth and respect.' },
  { icon: Zap, title: 'Speed', text: 'When things break, minutes matter. We arrange service as fast as we can, subject to engineer availability.' },
  { icon: ShieldCheck, title: 'Trust', text: 'Clear pricing, no small print games. Every plan is a straightforward maintenance service agreement.' },
  { icon: Users, title: 'Craft', text: 'Repairs and maintenance visits are arranged through our network of independent, qualified engineers.' },
];

const About = () => {
  return (
    <div className="bg-dhs min-h-screen">
      <Header />
      <main>
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-5 text-center">
            <div className="inline-flex items-center gap-2 bg-white border border-[#1F0068]/10 rounded-full px-3.5 py-1.5 text-xs font-semibold text-[#1F0068] mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF5C39]" /> Our story
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-[#1F0068] tracking-tight leading-[1.02]">
              Making home life <span className="text-[#FF5C39]">a little easier</span>, every day.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#1F0068]/70 max-w-3xl mx-auto">
              Domestic Home Services Limited provides home maintenance and repair service plans to residential customers across the UK. Our plans provide maintenance and breakdown support for household appliances, boilers, plumbing and drainage. Where assistance is required, we arrange the appropriate repair or maintenance service through our network of service providers. Our plans are maintenance service agreements and are <b>not insurance products</b>.
            </p>
          </div>
        </section>

        <section className="py-8">
          <div className="max-w-5xl mx-auto px-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((s) => (
                <div key={s.l} className="bg-white rounded-2xl p-6 border border-[#1F0068]/5 text-center">
                  <div className="text-3xl md:text-4xl font-extrabold text-[#1F0068]">{s.n}</div>
                  <div className="text-xs md:text-sm text-[#1F0068]/60 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-5 grid lg:grid-cols-2 gap-10 items-center">
            <div className="rounded-[36px] overflow-hidden aspect-[4/5] max-h-[600px] shadow-xl">
              <img src="https://images.pexels.com/photos/35290689/pexels-photo-35290689.jpeg?w=1200&q=80" alt="Our engineers" className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#FF5C39] mb-3">Why we exist</div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#1F0068] tracking-tight leading-tight">The old way didn’t work.</h2>
              <p className="mt-4 text-[#1F0068]/70 text-lg leading-relaxed">
                Traditional home cover has often meant surprise fees, small print traps and endless phone menus. We wanted something better — clear pricing, qualified engineers arranged through our service-provider network, and a service you'd actually recommend to your family.
              </p>
              <p className="mt-3 text-[#1F0068]/70 text-lg leading-relaxed">
                Our plans are straightforward maintenance service agreements. Repair and maintenance visits are arranged through our UK network of independent service providers. Customer service is available Monday to Friday, 9am to 5pm, on {`0113 548 7557`} or by email to hello@domestichomeservices.com.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-6xl mx-auto px-5">
            <div className="max-w-2xl mb-10">
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#FF5C39] mb-3">What we stand for</div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#1F0068] tracking-tight leading-tight">Four values, one promise.</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {values.map((v, i) => {
                const tints = ['bg-[#FFEDD6]', 'bg-[#FCE0EC]', 'bg-[#E8DDFF]', 'bg-[#FFE0D6]'];
                const Ico = v.icon;
                return (
                  <div key={v.title} className="dhs-card rounded-2xl p-6 border border-[#1F0068]/5 bg-[#FFF6EB]">
                    <div className={`w-12 h-12 rounded-xl ${tints[i]} flex items-center justify-center text-[#1F0068] mb-4`}>
                      <Ico className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-[#1F0068]">{v.title}</h3>
                    <p className="mt-2 text-sm text-[#1F0068]/70 leading-relaxed">{v.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-5">
            <div className="bg-[#1F0068] text-white rounded-[36px] p-8 md:p-14 text-center">
              <Award className="w-8 h-8 text-[#FF5C39] mx-auto mb-3" />
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Ready to choose a plan?</h2>
              <p className="mt-3 text-white/80 max-w-lg mx-auto">Simple monthly or annual maintenance service plans for UK households.</p>
              <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
                <Link to="/quote"><Button className="rounded-full bg-[#FF5C39] hover:bg-[#e0492b] text-white h-12 px-8 font-semibold">Get a quote <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
                <Link to="/contact"><Button variant="outline" className="rounded-full border-white text-white hover:bg-white hover:text-[#1F0068] h-12 px-8 font-semibold">Talk to us</Button></Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default About;
